import { getApiBaseUrl } from '../../lib/api';
/**
 * RRA 2025 Compliance Dashboard
 * 
 * Main dashboard for Renters Rights Act 2025 compliance management.
 * Shows compliance status, pending actions, and key metrics.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, AlertTriangle, Clock, FileText, Home,
  Bell, PawPrint, TrendingUp, CheckCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ComplianceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [notices, setNotices] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      
      // Fetch policy info (public endpoint)
      const policyRes = await fetch(`${API_URL}/api/rra-compliance/policy/info`);
      if (policyRes.ok) {
        setPolicyInfo(await policyRes.json());
      }
      
      // Fetch compliance summary (requires auth)
      if (token) {
        const summaryRes = await fetch(`${API_URL}/api/rra-compliance/dashboard/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (summaryRes.ok) {
          setSummary(await summaryRes.json());
        }
        
        // Fetch recent notices
        const noticesRes = await fetch(`${API_URL}/api/rra-compliance/notices?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (noticesRes.ok) {
          const data = await noticesRes.json();
          setNotices(data.notices || []);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getNoticeTypeBadge = (type) => {
    const badges = {
      'tenant_notice_to_quit': { label: 'Tenant Notice', variant: 'outline' },
      'section_8_mandatory': { label: 'Section 8 (Mandatory)', variant: 'destructive' },
      'section_8_discretionary': { label: 'Section 8 (Discretionary)', variant: 'warning' },
      'section_13_rent_increase': { label: 'Rent Increase', variant: 'secondary' },
    };
    const badge = badges[type] || { label: type, variant: 'outline' };
    return <Badge variant={badge.variant}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="compliance-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="rra-compliance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            Renters&apos; Rights Act 2025 Compliance
          </h1>
          <p className="text-muted-foreground">
            Manage compliance for England PRS tenancies
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/compliance/notices">
            <Button variant="outline" data-testid="view-notices-btn">
              <FileText className="h-4 w-4 mr-2" />
              View Notices
            </Button>
          </Link>
          <Link to="/compliance/pet-requests">
            <Button variant="outline" data-testid="view-pet-requests-btn">
              <PawPrint className="h-4 w-4 mr-2" />
              Pet Requests
            </Button>
          </Link>
        </div>
      </div>

      {/* Key Dates Banner */}
      {policyInfo && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary">RRA 2025 Effective Date</p>
                <p className="text-2xl font-bold">{formatDate(policyInfo.effective_date)}</p>
              </div>
              <div className="grid grid-cols-4 gap-6 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Protected Period</p>
                  <p className="font-semibold">{policyInfo.protected_period_months} months</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tenant Notice</p>
                  <p className="font-semibold">{policyInfo.tenant_notice_period_months} months</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pet Response SLA</p>
                  <p className="font-semibold">{policyInfo.pet_request_response_days} days</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Max Rent Advance</p>
                  <p className="font-semibold">{policyInfo.rent_advance_max_months} month</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-tenancies-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tenancies</p>
                  <p className="text-3xl font-bold">{summary.total_tenancies}</p>
                </div>
                <Home className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="pending-pet-requests-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Pet Requests</p>
                  <p className="text-3xl font-bold">{summary.pending_pet_requests}</p>
                </div>
                <PawPrint className={`h-8 w-8 ${summary.pending_pet_requests > 0 ? 'text-yellow-500' : 'text-muted-foreground/50'}`} />
              </div>
              {summary.pending_pet_requests > 0 && (
                <p className="text-xs text-yellow-600 mt-2">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Action required within 28 days
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="active-possession-cases-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Possession Cases</p>
                  <p className="text-3xl font-bold">{summary.active_possession_cases}</p>
                </div>
                <AlertTriangle className={`h-8 w-8 ${summary.active_possession_cases > 0 ? 'text-red-500' : 'text-muted-foreground/50'}`} />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="regime-breakdown-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">RRA 2025 Regime</p>
                  <p className="text-3xl font-bold">{summary.regime_breakdown?.rra_2025_v1 || 0}</p>
                </div>
                <ShieldCheck className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {summary.regime_breakdown?.pre_rra_2025 || 0} legacy tenancies
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Rates */}
      {summary?.compliance_rates && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Document Compliance Rates</CardTitle>
            <CardDescription>
              Percentage of tenancies with required documents served
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">How to Rent Guide</span>
                  <span className="text-sm font-medium">{summary.compliance_rates.how_to_rent_served}%</span>
                </div>
                <Progress value={summary.compliance_rates.how_to_rent_served} />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">Deposit Prescribed Info</span>
                  <span className="text-sm font-medium">{summary.compliance_rates.deposit_info_served}%</span>
                </div>
                <Progress value={summary.compliance_rates.deposit_info_served} />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">Gas Safety Certificate</span>
                  <span className="text-sm font-medium">{summary.compliance_rates.gas_safety_served}%</span>
                </div>
                <Progress value={summary.compliance_rates.gas_safety_served} />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">EPC Certificate</span>
                  <span className="text-sm font-medium">{summary.compliance_rates.epc_served}%</span>
                </div>
                <Progress value={summary.compliance_rates.epc_served} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Changes Info */}
      <Tabs defaultValue="changes" className="w-full">
        <TabsList>
          <TabsTrigger value="changes">Key Changes</TabsTrigger>
          <TabsTrigger value="notices">Recent Notices</TabsTrigger>
        </TabsList>
        
        <TabsContent value="changes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Renters&apos; Rights Act 2025 Key Changes</CardTitle>
            </CardHeader>
            <CardContent>
              {policyInfo?.key_changes && (
                <ul className="grid md:grid-cols-2 gap-3">
                  {policyInfo.key_changes.map((change, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-sm">{change}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notices">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Notices</CardTitle>
              <Link to="/compliance/notices">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {notices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No notices found</p>
              ) : (
                <div className="space-y-3">
                  {notices.map((notice) => (
                    <div 
                      key={notice.id} 
                      className="flex items-center justify-between p-3 rounded-lg border"
                      data-testid={`notice-item-${notice.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          {getNoticeTypeBadge(notice.notice_type)}
                          <p className="text-sm text-muted-foreground mt-1">
                            Created: {formatDate(notice.created_date)}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant={notice.status === 'served' ? 'default' : 'secondary'}
                      >
                        {notice.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/compliance/notices/new">
              <Button variant="outline" className="w-full h-20 flex-col" data-testid="create-notice-btn">
                <FileText className="h-6 w-6 mb-2" />
                Create Notice
              </Button>
            </Link>
            <Link to="/compliance/rent-increase">
              <Button variant="outline" className="w-full h-20 flex-col" data-testid="rent-increase-btn">
                <TrendingUp className="h-6 w-6 mb-2" />
                Rent Increase
              </Button>
            </Link>
            <Link to="/compliance/listing-scan">
              <Button variant="outline" className="w-full h-20 flex-col" data-testid="scan-listing-btn">
                <Home className="h-6 w-6 mb-2" />
                Scan Listing
              </Button>
            </Link>
            <Link to="/compliance/campaigns">
              <Button variant="outline" className="w-full h-20 flex-col" data-testid="campaigns-btn">
                <Bell className="h-6 w-6 mb-2" />
                Campaigns
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceDashboard;
